import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { loginSchema, type LoginInput } from '@rhpc/shared';
import { useAuthStore } from '@/stores/authStore';
import { AuthBrand } from '@/components/BrandMark';
import { Button, Input } from '@rhpc/ui';

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuthStore();
  const [serverError, setServerError] = useState<string | null>(null);

  const redirectMessage = (location.state as { message?: string } | null)?.message;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    mode: 'onBlur',
  });

  const onSubmit = async (data: LoginInput) => {
    setServerError(null);

    try {
      await login(data.email, data.password);
      const mustChange = useAuthStore.getState().user?.mustChangePassword;
      navigate(mustChange ? '/change-password' : '/dashboard');
    } catch {
      setServerError('Invalid email or password. Please try again.');
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg p-8">
      <div className="w-full max-w-[392px]">
        <AuthBrand title="Sign in" subtitle="Claim System" />

        <div className="rounded-lg border border-border bg-surface p-8 shadow-sm">
          {redirectMessage && (
            <div className="mb-4 rounded-md border-l-[3px] border-l-warn bg-warn-soft p-3" role="alert">
              <p className="text-sm text-warn">{redirectMessage}</p>
            </div>
          )}

          {serverError && (
            <div className="mb-4 rounded-md border-l-[3px] border-l-danger bg-danger-soft p-3" role="alert">
              <p className="text-sm text-danger">{serverError}</p>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
            <Input
              id="email"
              label="Email address"
              type="email"
              placeholder="you@robusthpc.com"
              autoComplete="email"
              error={errors.email?.message}
              {...register('email')}
            />

            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <span className="text-sm font-semibold text-text">Password</span>
                <Link to="/forgot-password" className="text-[13px] font-semibold text-primary-text hover:underline">
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                placeholder="Your password"
                autoComplete="current-password"
                error={errors.password?.message}
                {...register('password')}
              />
            </div>

            <Button type="submit" loading={isSubmitting} className="mt-2 w-full">
              Sign In
            </Button>
          </form>
        </div>

        <p className="mt-6 text-center text-sm text-text-2">
          Need an account? Contact an administrator for an invitation.
        </p>
      </div>
    </div>
  );
}
